import React, { useState, useEffect } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { Menu, X, Car, Phone, Mail, MapPin, Clock, ChevronRight, Wrench, ShieldCheck } from 'lucide-react';
import { SiInstagram, SiFacebook } from 'react-icons/si';
import { ROUTE_PATHS, cn } from '@/lib/index';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { motion, AnimatePresence } from 'framer-motion';
interface LayoutProps {
  children: React.ReactNode;
}
export function Layout({
  children
}: LayoutProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  const navItems = [{
    label: 'Startseite',
    path: ROUTE_PATHS.HOME
  }, {
    label: 'Leistungen',
    path: ROUTE_PATHS.SERVICES
  }, {
    label: 'Über uns',
    path: ROUTE_PATHS.ABOUT
  }, {
    label: 'Galerie',
    path: ROUTE_PATHS.GALLERY
  }, {
    label: 'Kontakt',
    path: ROUTE_PATHS.CONTACT
  }];
  return <div className="flex flex-col min-h-screen bg-background">
      {/* Top Bar - Hidden on Mobile */}
      <div className="hidden lg:block bg-secondary py-2 border-b border-border/50">
        <div className="container mx-auto px-4 flex justify-between items-center text-xs font-medium text-muted-foreground">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-1.5">
              <Phone className="w-3.5 h-3.5 text-primary" />
              <span>+491738572515</span>
            </div>
            <div className="flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-primary" />
              <span>Mosbach Baden Württemberg, Bavaria Nürnberg</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-primary" />
              <span>Mo-Fr: 08:00 - 18:00</span>
            </div>
          </div>
        </div>
      </div>

      {/* Header */}
      <header className={cn("sticky top-0 z-50 w-full transition-all duration-300", isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border shadow-sm py-3" : "bg-transparent py-5")}>
        <div className="container mx-auto px-4 flex justify-between items-center">
          <Link to={ROUTE_PATHS.HOME} className="flex items-center gap-2.5 group">
            <div className="bg-primary p-2 rounded-lg shadow-lg shadow-primary/20 group-hover:scale-105 transition-transform">
              <Car className="w-6 h-6 text-primary-foreground" />
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-bold tracking-tight leading-none text-foreground">
                Car Dent <span className="text-primary">Paintless</span>
              </span>
              <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mt-0.5">
                Ausbeulen Ohne Lackieren
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navItems.map(item => <NavLink key={item.path} to={item.path} className={({
            isActive
          }) => cn("text-sm font-medium transition-colors hover:text-primary relative py-1", isActive ? "text-primary" : "text-foreground/80")}>
                {({
              isActive
            }) => <>
                    {item.label}
                    {isActive && <motion.div layoutId="nav-underline" className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary" transition={{
                type: "spring",
                stiffness: 380,
                damping: 30
              }} />}
                  </>}
              </NavLink>)}
            <Button asChild size="sm" className="rounded-full px-6 shadow-md shadow-primary/10">
              <Link to={ROUTE_PATHS.CONTACT}>Anfrage senden</Link>
            </Button>
          </nav>

          {/* Mobile Nav Trigger */}
          <Sheet>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" className="text-foreground">
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px] border-l border-border">
              <SheetTitle className="text-left mb-8 flex items-center gap-2">
                <Car className="w-5 h-5 text-primary" />
                Menü
              </SheetTitle>
              <nav className="flex flex-col gap-4">
                {navItems.map(item => <NavLink key={item.path} to={item.path} className={({
                isActive
              }) => cn("text-lg font-medium p-3 rounded-xl transition-all", isActive ? "bg-primary/10 text-primary" : "text-foreground/70 hover:bg-secondary")}>
                    {item.label}
                  </NavLink>)}
                <div className="mt-8 pt-8 border-t border-border space-y-4">
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <Phone className="w-4 h-4 text-primary" />
                    <span className="text-sm font-medium">+49 (0) 123 456 789</span>
                  </div>
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <Mail className="w-4 h-4 text-primary" />
                    <span className="text-sm font-medium">info@cardent-repair.de</span>
                  </div>
                  <Button asChild className="w-full mt-4">
                    <Link to={ROUTE_PATHS.CONTACT}>Termin vereinbaren</Link>
                  </Button>
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-foreground text-background">
        <div className="container mx-auto px-4 pt-16 pb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
            {/* Brand Column */}
            <div className="space-y-6">
              <div className="flex items-center gap-2.5">
                <div className="bg-primary p-2 rounded-lg">
                  <Car className="w-6 h-6 text-primary-foreground" />
                </div>
                <span className="text-xl font-bold tracking-tight text-white">
                  Car Dent <span className="text-primary">Repair</span>
                </span>
              </div>
              <p className="text-background/60 text-sm leading-relaxed">
                Ihr Spezialist für professionelle Dellenentfernung ohne Lackieren. 
                Höchste Präzision, Kosteneffizienz und Werterhalt Ihres Fahrzeugs seit über 15 Jahren.
              </p>
              <div className="flex items-center gap-4">
                <a href="#" className="p-2 rounded-full bg-white/5 hover:bg-primary transition-colors">
                  <SiInstagram className="w-5 h-5" />
                </a>
                <a href="#" className="p-2 rounded-full bg-white/5 hover:bg-primary transition-colors">
                  <SiFacebook className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-white font-semibold mb-6">Schnellzugriff</h4>
              <ul className="space-y-3">
                {navItems.map(item => <li key={item.path}>
                    <Link to={item.path} className="text-background/60 hover:text-primary transition-colors text-sm flex items-center gap-2 group">
                      <ChevronRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                      {item.label}
                    </Link>
                  </li>)}
              </ul>
            </div>

            {/* Services Column */}
            <div>
              <h4 className="text-white font-semibold mb-6">Unsere Leistungen</h4>
              <ul className="space-y-3">
                {['Hagelschaden-Reparatur', 'Parkdellen-Service', 'Smart Repair', 'Leasing-Rückläufer Check'].map(service => <li key={service}>
                    <Link to={ROUTE_PATHS.SERVICES} className="text-background/60 hover:text-primary transition-colors text-sm flex items-center gap-2 group">
                      <ChevronRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                      {service}
                    </Link>
                  </li>)}
              </ul>
            </div>

            {/* Contact Column */}
            
          </div>

          {/* Trust Badges */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-8 border-y border-white/10 mb-8">
            <div className="flex flex-col items-center gap-2 text-center">
              <ShieldCheck className="w-8 h-8 text-primary" />
              <span className="text-[10px] uppercase font-bold tracking-wider">Zertifizierter Betrieb</span>
            </div>
            <div className="flex flex-col items-center gap-2 text-center">
              <Wrench className="w-8 h-8 text-primary" />
              <span className="text-[10px] uppercase font-bold tracking-wider">Moderne Technik</span>
            </div>
            <div className="flex flex-col items-center gap-2 text-center">
              <Car className="w-8 h-8 text-primary" />
              <span className="text-[10px] uppercase font-bold tracking-wider">Alle Marken</span>
            </div>
            <div className="flex flex-col items-center gap-2 text-center">
              <Clock className="w-8 h-8 text-primary" />
              <span className="text-[10px] uppercase font-bold tracking-wider">Express Service</span>
            </div>
          </div>

          {/* Copyright */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-background/40">
            <p>© 2026 Car dent paintless repair. Alle Rechte vorbehalten.</p>
            <div className="flex gap-6">
              <Link to="#" className="hover:text-primary transition-colors">Impressum</Link>
              <Link to="#" className="hover:text-primary transition-colors">Datenschutz</Link>
              <Link to="#" className="hover:text-primary transition-colors">AGB</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}